import { CreditCardStatus } from '@nemuru-eng/js-api-client';

import { CheckoutSessionLinks } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/components/ConditionsAndProgress/CheckoutSessionLinks/CheckoutSessionLinks';
import { ProgressComponents } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/components/ConditionsAndProgress/ProgressBarAndTimeline/_shared/ProgressComponents';
import { ProgressWorkflowAccordion } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/components/ConditionsAndProgress/ProgressBarAndTimeline/ProgressWorkflow/WithProgressWorkflow/components/ProgressWorkflowAccordion';
import { PaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/PaymentMethodDetail/PaymentMethodDetailEntity';
import { AbstractProgress } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/Progress/AbstractProgress';
import { AbstractProgressWorkflow } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/ProgressWorkflow/AbstractProgressWorkflow';
import { FlexColumn } from '@/components/shared/components/FlexColumn';
import { FlexRow } from '@/components/shared/components/FlexRow';
import { HorizontalProgressBar } from '@/components/shared/components/HorizontalProgressBar';
import { StatusTag } from '@/components/shared/components/StatusTag/StatusTag';
import { Theme } from '@/config/types';
import { useUserContext } from '@/contexts/UserContext/UserContext';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface WithProgressWorkflowProps {
  paymentMethodDetailEntity: PaymentMethodDetailEntity;
}

export const WithProgressWorkflow = ({ paymentMethodDetailEntity }: WithProgressWorkflowProps) => {
  const { palette } = useCustomTheme();
  const { userEntity } = useUserContext();

  const progress = paymentMethodDetailEntity.progress;

  if (progress instanceof AbstractProgress) {
    throw new Error('Bad implementation of WithProgressWorkflow with AbstractProgress');
  }

  if (!progress) {
    return null;
  }

  return (
    <ProgressComponents.Layout
      progressLabel="APPLICATION_PROGRESS"
      StatusTag={
        progress?.isValid && (
          <FlexRow>
            <StatusTag
              type={progress.statusDefinition.colorPaletteName}
              colorType={progress.statusDefinition.colorType as 'main' | 'dark' | 'light'}
              labelTextId={progress.statusDefinition.DESCRIPTION_T_ID}
            />
          </FlexRow>
        )
      }
      ProgressBar={
        <HorizontalProgressBar
          progressValue={progress?.progressPercentage ?? 0}
          color={getProgressColor({
            progress,
            palette,
          })}
          style={{ margin: 0 }}
        />
      }
    >
      <FlexColumn width={'100%'} gridGap={8}>
        <ProgressWorkflowAccordion progress={progress} />
        {userEntity && (
          <CheckoutSessionLinks
            paymentMethodDetailEntity={paymentMethodDetailEntity}
            userType={userEntity.user.userType}
          />
        )}
      </FlexColumn>
    </ProgressComponents.Layout>
  );
};

const getProgressColor = ({ progress, palette }: { progress: AbstractProgressWorkflow; palette: Theme['palette'] }) => {
  if (
    [CreditCardStatus.DENIED, CreditCardStatus.CANCELLED, CreditCardStatus.DESISTED, CreditCardStatus.EXPIRED].includes(
      progress.status,
    )
  ) {
    return palette.grey[500];
  }

  if (progress.isCompleted) {
    return palette.success.main;
  }

  if (progress.isActive) {
    return palette.secondary.main;
  }

  return palette.grey[500];
};
